import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { ComboBox, Input } from '@gokrew/ui';
import {
  Plus,
  GripVertical,
  UserRound,
  Columns3,
  LayoutList,
  Search,
} from 'lucide-react';
import {
  getCrmDeals,
  getCrmMembers,
  getCrmStages,
  moveCrmDeal,
} from '@/api/crm';
import type { CrmDeal, CrmDealStage } from '@/api/crm';
import {
  arrDollars,
  personInitials,
  personName,
  useCrmFormat,
  DEAL_STAGES,
} from './crm-format';
import { CrmEmpty, CrmError } from './CrmShared';
import { CrmStageRate } from './CrmStageRate';
import styles from './Crm.module.css';
/** A card matches when its deal, company or contact name contains the text. */
const matches = (deal: CrmDeal, needle: string) =>
  !needle ||
  [deal.name, deal.company?.name, deal.contact?.name].some((value) =>
    value?.toLowerCase().includes(needle)
  );
export const CrmDeals = ({
  search,
  filter,
  onSearch,
  onDeal,
  onCreate,
}: {
  /** Debounced text sent to the server, for pipelines over one page. */
  search: string;
  /** Live text from the search box: filters the loaded cards on every key. */
  filter: string;
  onSearch: (text: string) => void;
  onDeal: (id: string) => void;
  onCreate: (stage?: CrmDealStage) => void;
}) => {
  const { t, arr } = useCrmFormat();
  const client = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [owner, setOwner] = useState('all');
  const [layout, setLayout] = useState('board');
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [moving, setMoving] = useState<string | null>(null);
  const [error, setError] = useState('');
  const stageFilter = params.get('stage') || 'all';
  const scope = {
    ...(owner === 'all' ? {} : { ownerUserId: owner }),
    ...(stageFilter === 'all' ? {} : { stage: stageFilter }),
    pageSize: 200,
  };
  // The board loads the whole pipeline and filters it as the user types.
  const deals = useQuery({
    queryKey: ['crm', 'deals', owner, stageFilter],
    queryFn: () => getCrmDeals(scope),
    placeholderData: keepPreviousData,
  });
  // Only a pipeline over one page also asks the server, which sees every deal.
  const truncated = !!deals.data && deals.data.total > deals.data.items.length;
  const searched = useQuery({
    queryKey: ['crm', 'deals', owner, stageFilter, search],
    queryFn: () => getCrmDeals({ ...scope, search }),
    enabled: truncated && !!search.trim(),
  });
  const members = useQuery({
    queryKey: ['crm', 'members'],
    queryFn: getCrmMembers,
  });
  const move = async (deal: CrmDeal, stage: CrmDealStage) => {
    if (moving || deal.stage === stage) return;
    setMoving(deal.id);
    setError('');
    try {
      await moveCrmDeal(deal, stage);
      await client.invalidateQueries({ queryKey: ['crm'] });
    } catch (err) {
      setError(
        t(
          (err as { response?: { status?: number } }).response?.status === 409
            ? 'crm.conflict'
            : 'crm.save_error'
        )
      );
      await deals.refetch();
    } finally {
      setMoving(null);
      setDragging(null);
      setOver(null);
    }
  };
  const needle = filter.trim().toLowerCase();
  const loaded =
    truncated && needle && searched.data ? searched.data : deals.data;
  const items = (loaded?.items ?? []).filter((deal) => matches(deal, needle));
  const stageRates = useQuery({
    queryKey: ['crm', 'stages'],
    queryFn: getCrmStages,
  });
  const rateOf = (stage: string) =>
    stageRates.data?.find((s) => s.stage === stage)?.winProbability ?? 0;
  const columns = DEAL_STAGES;
  return (
    <>
      <div className={styles.boardToolbar}>
        <div className={styles.filters}>
          <div className={styles.boardSearch}>
            <Input
              IconPrefix={Search}
              aria-label={t('crm.workspace.search_deals')}
              placeholder={t('crm.workspace.search_deals')}
              value={filter}
              onChange={(event) => onSearch(event.target.value)}
            />
          </div>
          <ComboBox
            size="sm"
            value={owner}
            allowUnselect={false}
            placeholder={t('crm.workspace.all_owners')}
            options={[
              { value: 'all', label: t('crm.workspace.all_owners') },
              ...(members.data ?? []).map((member) => ({
                value: member.userId,
                label:
                  [member.firstName, member.lastName]
                    .filter(Boolean)
                    .join(' ') || member.email,
              })),
            ]}
            onChange={(value) => setOwner(value || 'all')}
          />
          <ComboBox
            size="sm"
            value={stageFilter}
            allowUnselect={false}
            options={[
              { value: 'all', label: t('crm.workspace.all_stages') },
              ...DEAL_STAGES.map((value) => ({
                value,
                label: t(`crm.workspace.stages.${value}`),
              })),
            ]}
            onChange={(value) => {
              const next = new URLSearchParams(params);
              if (!value || value === 'all') next.delete('stage');
              else next.set('stage', value);
              setParams(next);
            }}
          />
        </div>
        <div className={styles.layoutSwitch}>
          <button
            aria-label={t('crm.workspace.kanban_view')}
            aria-pressed={layout === 'board'}
            onClick={() => setLayout('board')}
          >
            <Columns3 />
          </button>
          <button
            aria-label={t('crm.workspace.list_view')}
            aria-pressed={layout === 'list'}
            onClick={() => setLayout('list')}
          >
            <LayoutList />
          </button>
        </div>
      </div>
      <div className={styles.pipelineMeta}>
        <span>
          {t('crm.workspace.deal_count', {
            count: needle ? items.length : (deals.data?.total ?? 0),
          })}
        </span>
        <strong>
          {arr(
            items
              .filter((item) => !['won', 'lost'].includes(item.stage))
              .reduce((sum, item) => sum + arrDollars(item.arrCents) * 100, 0)
          )}
          <small>{t('crm.workspace.annual_pipeline')}</small>
        </strong>
        <strong title={t('crm.workspace.weighted_hint')}>
          {arr(
            items
              .filter((item) => !['won', 'lost'].includes(item.stage))
              .reduce(
                (sum, item) =>
                  sum +
                  (arrDollars(item.arrCents) * 100 * rateOf(item.stage)) / 100,
                0
              )
          )}
          <small>{t('crm.workspace.weighted_pipeline')}</small>
        </strong>
        <p>{t('crm.workspace.drag_hint')}</p>
      </div>
      {error && (
        <p className={styles.errorBox} role="alert">
          {error}
        </p>
      )}
      {deals.isPending ? (
        <p role="status" className={styles.loading}>
          {t('crm.loading')}
        </p>
      ) : deals.isError ? (
        <CrmError retry={() => void deals.refetch()} />
      ) : (
        <>
          {!needle && truncated && (
            <p className={styles.notice}>
              {t('crm.workspace.board_limit', {
                count: items.length,
                total: deals.data.total,
              })}
            </p>
          )}
          <div
            className={layout === 'board' ? styles.board : styles.dealsList}
            aria-label={t('crm.workspace.deal_pipeline')}
          >
            {columns.map((stage) => {
              const cards = items.filter((deal) => deal.stage === stage);
              return (
                <section
                  key={stage}
                  className={`${styles.boardColumn} ${over === stage ? styles.dropActive : ''}`}
                  aria-label={t(`crm.workspace.stages.${stage}`)}
                  onDragOver={(event) => {
                    if (dragging) {
                      event.preventDefault();
                      event.dataTransfer.dropEffect = 'move';
                      setOver(stage);
                    }
                  }}
                  onDragLeave={(event) => {
                    if (
                      !event.currentTarget.contains(event.relatedTarget as Node)
                    )
                      setOver(null);
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    const id = event.dataTransfer.getData(
                      'application/x-kollab-crm-deal'
                    );
                    const deal = items.find((item) => item.id === id);
                    if (deal) void move(deal, stage);
                  }}
                >
                  <div className={styles.columnHeading}>
                    <span className={styles[`dot_${stage}`]} />
                    <h2>{t(`crm.workspace.stages.${stage}`)}</h2>
                    <span className={styles.columnCount}>{cards.length}</span>
                    <CrmStageRate stage={stage} value={rateOf(stage)} />
                  </div>
                  <p className={styles.columnValue}>
                    {arr(
                      cards.reduce(
                        (sum, card) => sum + arrDollars(card.arrCents) * 100,
                        0
                      )
                    )}
                    <span>{t('crm.workspace.per_year')}</span>
                  </p>
                  <div className={styles.columnCards}>
                    {cards.map((deal) => (
                      <article
                        key={deal.id}
                        draggable={!moving}
                        aria-busy={moving === deal.id}
                        className={`${styles.dealCard} ${dragging === deal.id ? styles.dragging : ''}`}
                        onClick={(event) => {
                          const target = event.target as Element;
                          // The name button opens it itself, and a click from
                          // a portal (outside the card) must not open it.
                          if (
                            event.currentTarget.contains(target) &&
                            !target.closest('button, label, input, a')
                          )
                            onDeal(deal.id);
                        }}
                        onDragStart={(event) => {
                          event.dataTransfer.effectAllowed = 'move';
                          event.dataTransfer.setData(
                            'application/x-kollab-crm-deal',
                            deal.id
                          );
                          setDragging(deal.id);
                        }}
                        onDragEnd={() => {
                          setDragging(null);
                          setOver(null);
                        }}
                      >
                        <div className={styles.cardTopline}>
                          <span className={styles.cardProduct}>
                            {t(`crm.products.${deal.product}`)}
                          </span>
                          <GripVertical aria-hidden="true" />
                        </div>
                        <button
                          className={styles.dealCardName}
                          onClick={() => onDeal(deal.id)}
                        >
                          {deal.name}
                        </button>
                        <div className={styles.cardValue}>
                          <strong>{arr(deal.arrCents)}</strong>
                          <span>{t('crm.workspace.per_year')}</span>
                          {deal.owner && (
                            <span
                              className={styles.ownerBadge}
                              title={t('crm.workspace.owner_named', {
                                name: personName(deal.owner),
                              })}
                              aria-label={t('crm.workspace.owner_named', {
                                name: personName(deal.owner),
                              })}
                            >
                              {personInitials(deal.owner)}
                            </span>
                          )}
                        </div>
                        {deal.contact && (
                          <p className={styles.cardContact}>
                            <UserRound />
                            {deal.contact.name}
                          </p>
                        )}
                      </article>
                    ))}
                    {cards.length === 0 && (
                      <button
                        className={styles.columnEmpty}
                        onClick={() => onCreate(stage)}
                      >
                        <Plus />
                        {t('crm.workspace.add_opportunity')}
                      </button>
                    )}
                  </div>
                </section>
              );
            })}
          </div>
          {items.length === 0 && needle && (
            <CrmEmpty
              title={t('crm.no_results')}
              description={t('crm.try_search')}
            />
          )}
        </>
      )}
    </>
  );
};
